<template>
  <div class="view history">
    <div class="header-search">
      <div class="content title">{{ $t('headerSearchAddCompany') }}</div>
      <div class="content header-content">
        <div class="input-container">
          <input
            v-model="inputSearchCompany"
            type="text"
            :placeholder="$t('companySearchPlaceholder')"
            class="input"
            @keydown.enter="() => getCompany(inputSearchCompany)"
          />
          <div class="icon-container">
            <IconSearch class="icon-search" @click="() => getCompany(inputSearchCompany)" />
          </div>
        </div>

        <div v-if="user.isMainUser" class="main-button hide-medium" @click="$router.push('/importCompanies')">
          <IconImport class="icon icon-import"></IconImport>{{ $t('import') }}
        </div>
      </div>
    </div>
    <div class="content">
      <div class="header">
        <div class="link-button"></div>
      </div>

      <div class="sub-header no-scroll-bar">
        <RouterLink class="link" :class="{ active: page.includes('manual') }" to="/history/manual">{{ $t('manual') }}</RouterLink>
        <RouterLink class="link" :class="{ active: page.includes('group') }" to="/history/group">{{ $t('group') }}</RouterLink>
      </div>
      <div class="header no-scroll-bar">
        <div class="form-container filter">
          <div class="input-title">{{ $t('filterByManager') }}</div>
          <Dropdown
            class="dropdown"
            :config="{ inputSize: 48 }"
            :values="managers"
            :placeholder="$t('selectAManager')"
            :on-change="value => (managerFilter = value)"
            :value="managerFilter"
          >
            <template #placeholder="{ value, isPlaceholder }">
              <div v-if="!isPlaceholder">{{ value.firstName }} {{ value.lastName }}</div>
              <div v-else class="placeholder">{{ value }}</div>
            </template>
            <template #choices="{ choice }">
              <div>{{ choice.firstName }} {{ choice.lastName }}</div>
            </template>
          </Dropdown>
        </div>
        <div class="link-button reset-button" @click="resetFilters">{{ $t('resetFilters') }} <ResetIcon class="icon-reset" /></div>
      </div>

      <HistoryMonitoringTable
        v-if="loaded && page.includes('group')"
        :key="'group'"
        :filters="getFilters"
        :users="users"
        :packages="packages"
        :config="{ pagination: true }"
      />
      <HistoryMonitoringTable
        v-else-if="loaded && page.includes('manual')"
        :key="'manual'"
        :filters="getFilters"
        :users="users"
        :packages="packages"
        :config="{ pagination: true, isManual: true }"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Dropdown from '@/components/Dropdown';
import HistoryMonitoringTable from '@/components/tables/HistoryMonitoringTable';

export default {
  components: { HistoryMonitoringTable, Dropdown },

  props: {
    page: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      loaded: false,
      alerts: [],
      packages: [],
      users: [],
      inputSearchCompany: '',
      search: '',
      managers: [],
      managerFilter: null,
    };
  },

  computed: {
    getFilters() {
      return {
        isFiltered: this.search,
        search: this.search,
        managerPublicId: this.managerFilter ? this.managerFilter.id : '',
      };
    },

    ...mapGetters(['isLogged', 'user']),

    getMonitoringInfos() {
      const info = this.packages.find(value => value.publicId === this.monitoringLevel);
      return info;
    },

    getManagerInfos() {
      const info = this.users.find(value => value.id === this.manager);
      return info;
    },
  },

  async created() {
    this.packages = (await this.$store.dispatch('getPackagesLevel')).items;
    this.users = (await this.$store.dispatch('getUsers', { id: this.user.client.id })).users;
    this.managers = this.users;
    this.loaded = true;
  },

  methods: {
    getCompany() {
      if (this.inputSearchCompany.trim().length === 0) return;

      const isSiren = Number(this.inputSearchCompany.replace(/\s/g, ''));
      if (isSiren && isSiren.toString().length >= 6) {
        this.inputSearchCompany = this.inputSearchCompany.replace(/\s/g, '');
      }

      this.$router.push({
        name: 'search',
        params: { searchedCompanies: this.inputSearchCompany },
      });
    },

    resetFilters() {
      this.managerFilter = null;
    },
  },
};
</script>

<style scoped lang="scss">
@use '@/styles/colors.scss';
@use '@/styles/responsive.scss';
.view {
  padding: 144px 0 64px;
  box-sizing: border-box;

  .header-search {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    padding: 128px 0 50px;
    background: colors.$main-color;
    margin-bottom: 32px;
    margin-top: -144px;

    .header-content {
      display: flex;
    }

    .title {
      width: 100%;
      font-weight: 500;
      font-size: 20px;
      text-align: left;
      color: colors.$white-color;
      display: flex;
      justify-content: flex-start;
      margin: 0 0 16px;
    }

    .input-container {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-grow: 1;
      margin: 0 32px 0 0;
      box-sizing: border-box;

      .icon-container {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 48px;
        height: 46px;
        padding: 0;
        color: colors.$lightblue-color;
        border-radius: 4px;
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
        border: solid 1px colors.$lightgrey-color;
        background: colors.$white-color;
        border-left: none;

        &:hover {
          cursor: pointer;

          .icon-search {
            transform: scale(1.1);
          }
        }
      }

      .input {
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
        border-right: none;
        padding-right: 0;
        margin: 0;
        text-overflow: ellipsis;
      }
    }

    .main-button {
      background: colors.$lightgreen-color;
      min-width: 48px;
      padding: 0 18px;
      border-radius: 5px;

      .icon {
        margin-right: 6px;
      }
    }
  }

  .content {
    .input {
      width: 100%;
      height: 48px;
      margin: 0 0 16px;
      padding: 12px 16px;
      box-sizing: border-box;
      color: colors.$text-color;
      border: solid 1px colors.$lightgrey-color;
      border-radius: 4px;
      background: colors.$white-color;

      &::placeholder {
        color: colors.$lightgrey-color;
      }

      &.incorrect {
        border-color: colors.$error-color;
      }

      &.search {
        min-width: 264px;
      }
    }

    .form-container {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      justify-content: center;
      margin-right: 8px;

      &.search {
        flex-grow: 1;
      }
    }

    .sub-header {
      margin: 0;
    }

    .header {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      margin-top: 40px;

      .link-button {
        color: colors.$lightgrey-color;
        margin-left: 24px;
      }

      .main-button {
        background: colors.$main-color;
      }
    }
  }
}

.filter {
  width: 30%;

  .input-title {
    width: 100%;
    color: colors.$darkgrey-color;
    font-weight: 500;
    font-size: 16px;
    margin-bottom: 10px;
  }
}

@media only screen and (max-width: responsive.$layout-breakpoint-small) {
}
</style>
